/**
 * Platform Config Service
 * ============================
 *
 * extends our baseConfig but focus only on Platform config here
 * - typed config
 * - inject defaults for platform
 * - inject env variables
 */
import type { Signal } from '@angular/core';
import { computed, Inject, inject, Injectable, Optional } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';

import type { MenuItemType } from '@evc/web-components';
import {
  ConfigService,
  deepMerge,
  ENV_VARIABLES,
  ModalService,
  ThemeService,
} from '@evc/web-components';

import { AuthConfig } from '../../clients/core/auth/auth.type';
import { AuthInterceptorExclusionService } from '../../clients/core/auth/interceptor/exclusion.service';
import { AUTH_CONFIG } from '../../clients/core/auth/providers/auth.providers';
import { I18nPlatformService } from '../i18n/i18n.service';
import type { AppIdentity } from '../types/app-identity.type';
import type { ApplicationType } from '../types/user-menu.type';
import { defaultPlatformConfigFactory, getApiConfig, getI18nConfig } from './config.default';
import type { DefaultDomain, PlatformConfig } from './config.type';
import { PlatformEnv } from './env.type';

export type PlatformEnvExtended = PlatformEnv & Record<string, unknown>;

/** remap .env config in final config format */
function envToConfig(environment: Partial<PlatformEnv>): Partial<PlatformConfig> {
  const {
    ENV,
    APP_NAME,
    APP_TITLE,
    APP_LOGO,
    USE_CONNECT_PLATFORM: USE_CONNECT_PLATFORM,
  } = environment;

  const appIdentity: AppIdentity = {
    name: APP_NAME,
    title: APP_TITLE || APP_NAME,
    logo: APP_LOGO,
  };

  return {
    env: ENV,
    useConnect: USE_CONNECT_PLATFORM,
    app: appIdentity,
  } as Partial<PlatformConfig>; // enforce because will be nice after merge
}

@Injectable({
  providedIn: 'root',
})
export class PlatformConfigService extends ConfigService<PlatformConfig & Record<string, unknown>> {
  #authExclusionService = inject(AuthInterceptorExclusionService);

  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    @Optional() @Inject(ENV_VARIABLES) private envVars: PlatformEnvExtended,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    @Optional() @Inject(AUTH_CONFIG) authConfig: AuthConfig,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    themeService: ThemeService,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    modalService: ModalService,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    i18nPlatformService: I18nPlatformService,
  ) {
    const environment = envVars ?? { ENV: 'production' };

    const defaultPlatformConfig = defaultPlatformConfigFactory(
      environment,
      themeService,
      modalService,
      i18nPlatformService,
    );
    const auth = authConfig;
    const api = getApiConfig(environment);
    const i18n = getI18nConfig(environment);

    const config = deepMerge(defaultPlatformConfig, envToConfig(environment), {
      auth,
      api,
      i18n,
    }) as PlatformConfig;

    super(config, environment);
  }
  /** may init flows diffently if *connect* or *legacy* mode
   * - connect: init auth, users, organizations etc
   * - legacy, we may just wait config to be provided by host app
   */
  useConnect = computed(() => this.getComputed('useConnect')() === true);

  /** we may extract `applications` from our usermodel vs platformComponent input
   * so this config handle this shortcut
   */
  get applications(): ApplicationType[] {
    return this.get('applications') ?? [];
  }

  get appIdentity(): Signal<AppIdentity> {
    return computed(() => {
      const { title, logo, version } = this.getComputed('app')();

      return { title, logo, version };
    });
  }

  getMenu<T extends keyof PlatformConfig['menus']>(type: T): Signal<PlatformConfig['menus'][T]> {
    return computed(() => {
      const menus = this.getComputed('menus')();

      return menus[type];
    });
  }

  /** Merge menu items - override if same `key`
   * * expect change soon here to unify all menus - actualy those are the only one with default + group title+items
   */
  updateMenu(type: 'settings' | 'help', items: MenuItemType[]): void {
    const menus = { ...this.get('menus') };
    const menu = menus[type];
    if (!menu) return;

    if (type === 'help') {
      const oldItems = menu.items || [];
      const newItems = items.filter(
        (item) => !oldItems.some((oldItem) => oldItem.key === item.key),
      );
      menu.items = [...newItems, ...oldItems];
    }

    // merge all items - override if same key
    const existingItems = menu.items || [];
    const itemMap = new Map<string, MenuItemType>();
    existingItems.forEach((item) => itemMap.set(item.key, item));
    items.forEach((item) => itemMap.set(item.key, item));

    this._config.update((_config) => {
      const config = { ..._config };
      config.menus[type].items = Array.from(itemMap.values());

      return config;
    });
  }

  /** return our uri prefixed with correct domain (per env)
   * safely because auto stop if no api config found (!useConnect)
   */
  safelyGetApiUrl$(
    domain: DefaultDomain,
    endpoint: string,
    options: GetApiUrlOptions = {},
  ): Observable<string> {
    const baseURI = this.get('api')?.uris[domain];

    if (!baseURI) return EMPTY;

    const fullUri = `${baseURI}${endpoint}`;

    if (options.skipAuth) {
      this.#authExclusionService.markAsUnprotected(fullUri);
    }

    return of(fullUri);
  }

  /** safe way to check an environment variable
   * use readonly configuration : not affected by this.set
   */
  getEnv<K extends keyof PlatformEnvExtended>(key: K, autoFormat = true): PlatformEnvExtended[K] {
    const value = this.envVars?.[key];

    if (!autoFormat) return value;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const autoValue = value as any;
    if ([true, 'true', '1', 1].includes(autoValue)) return true;
    if ([false, 'false', '0', 0].includes(autoValue)) return false;
    if (autoValue === '') return null;

    try {
      const tryNumber = parseInt(autoValue, 10);
      if (!isNaN(tryNumber)) return tryNumber;
    } catch {}

    return value;
  }
}

export type GetApiUrlOptions = {
  skipAuth?: boolean;
};
